import { Badge, Button, Card, LoadingContainer, Modal } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui/Badge';
import {
  sysadminService,
  type AudioCasoDTO,
  type CasoCorreccionDTO,
  type CasosCorreccionResponse,
  type TipoCasoCorreccion,
} from '@/services/sysadmin.service';
import { GestionAsesoriaModal } from './GestionAsesoriaModal';
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, RefreshCw, Search, UserCog } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Correcciones de asesorías (solo sysadmin):
 * - Audios subidos a la asesoría equivocada (p. ej. quien escribió "2" en el formulario creyendo que
 *   era su segunda asesoría): se mueven a la asesoría correcta de quien los subió.
 * - Cambios de asesor que no se reflejaron: se cambia el dueño de la asesoría.
 * - Asesorías sin audios (finalizadas, en revisión o en curso hace mucho): se revisan, y suelen ser
 *   el destino de los audios mal ubicados.
 * Cada cambio se aplica en producción al confirmar.
 */

const TABS: { id: string; label: string; tipos: TipoCasoCorreccion[] }[] = [
  { id: 'ajenos', label: 'Audios de otro asesor', tipos: ['AUDIOS_AJENOS'] },
  { id: 'finalizadas', label: 'Finalizadas sin audios', tipos: ['FINALIZADA_SIN_AUDIOS'] },
  { id: 'otras', label: 'En revisión / en curso sin audios', tipos: ['REVISION_SIN_AUDIOS', 'EN_CURSO_ANTIGUA'] },
];

const TIPO_LABEL: Record<TipoCasoCorreccion, string> = {
  AUDIOS_AJENOS: 'Audios de otro asesor',
  FINALIZADA_SIN_AUDIOS: 'Finalizada sin audios',
  REVISION_SIN_AUDIOS: 'En revisión sin audios',
  EN_CURSO_ANTIGUA: 'En curso hace más de 45 días sin audios',
};

const ESTADO: Record<string, [string, BadgeVariant]> = {
  FINALIZADA: ['Finalizada', 'green'],
  EN_CURSO: ['En curso', 'blue'],
  EN_REVISION: ['En revisión', 'warning'],
  REQUIERE_CORRECCIONES: ['Requiere correcciones', 'red'],
  SIN_EVALUAR: ['Sin evaluar', 'gray'],
  REVERTIDA: ['Revertida', 'gray'],
};

const ordenTxt = (o: number | null) => (o === 1 ? 'Diagnóstico' : o ? `Asesoría ${o - 1}` : 'Sin orden');
const dur = (seg: number) => {
  const s = Math.round(seg || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', r ? `${r}s` : ''].filter(Boolean).join(' ') || '0s';
};
const horasTxt = (h: number | null) => `${(Math.round((h ?? 0) * 100) / 100).toLocaleString('es-CO')} h`;
const diasTxt = (d: number) => (d === 0 ? 'mismo día' : d === 1 ? '1 día' : `${d} días`);

type Accion =
  | { tipo: 'mover'; audio: AudioCasoDTO; caso: CasoCorreccionDTO; destinoId: number }
  | { tipo: 'moverVarios'; caso: CasoCorreccionDTO; movimientos: { audio: AudioCasoDTO; destinoId: number }[] }
  | { tipo: 'dueno'; caso: CasoCorreccionDTO; asesorId: number };

interface Resultado {
  ok: boolean;
  texto: string;
  advertencias: string[];
}

export function CorreccionesAsesoriasPage() {
  const [data, setData] = useState<CasosCorreccionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState('ajenos');
  const [q, setQ] = useState('');
  const [accion, setAccion] = useState<Accion | null>(null);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [gestionId, setGestionId] = useState<number | null>(null);
  const [absorberId, setAbsorberId] = useState<number | null>(null);
  const [abrirId, setAbrirId] = useState('');

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await sysadminService.obtenerCasosCorreccion());
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar los casos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const nombres = useMemo(() => new Map((data?.asesores ?? []).map((a) => [a.asesorId, a.nombre])), [data]);
  const nombre = (id: number | null) => (id ? (nombres.get(id) ?? `Asesor #${id}`) : '—');

  // Audios mal ubicados que sugieren cada asesoría como destino (para las tarjetas "sin audios").
  const posibles = useMemo(() => {
    const m = new Map<number, { audioId: number; desde: number; fecha: string; dias: number }[]>();
    for (const c of data?.casos ?? []) {
      for (const au of c.audios) {
        for (const cand of au.candidatos) {
          const lista = m.get(cand.asesoriaId) ?? [];
          lista.push({ audioId: au.audioId, desde: c.asesoriaId, fecha: au.fechaGrabacion, dias: cand.dias });
          m.set(cand.asesoriaId, lista);
        }
      }
    }
    return m;
  }, [data]);

  const casosTab = useMemo(() => {
    const t = TABS.find((x) => x.id === tab)!;
    const term = q.trim().toLowerCase().replace(/^#/, '');
    return (data?.casos ?? [])
      .filter((c) => t.tipos.includes(c.tipo))
      .filter((c) => {
        if (!term) return true;
        const hay = [c.empresa, c.asesoriaId, nombre(c.asesorId), ...c.audios.map((a) => `${a.audioId} ${nombre(a.subioId)}`)]
          .join(' ')
          .toLowerCase();
        return hay.includes(term);
      })
      .sort((a, b) => {
        const ajenos = (c: CasoCorreccionDTO) => c.audios.filter((x) => !x.esDelDueno).length;
        const ninguno = (c: CasoCorreccionDTO) => (c.audios.length > 0 && ajenos(c) === c.audios.length ? 1 : 0);
        return ninguno(b) - ninguno(a) || ajenos(b) - ajenos(a) || a.empresa.localeCompare(b.empresa);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tab, q, nombres]);

  const aplicar = async () => {
    if (!accion) return;
    setAplicando(true);
    try {
      if (accion.tipo === 'dueno') {
        const r = await sysadminService.cambiarDuenoAsesoria(accion.caso.asesoriaId, accion.asesorId);
        setResultado({
          ok: true,
          texto: `Asesoría #${r.asesoriaId}: dueño cambiado de ${r.antes.nombre} a ${r.despues.nombre}.`,
          advertencias: [],
        });
      } else {
        const movs = accion.tipo === 'mover' ? [{ audio: accion.audio, destinoId: accion.destinoId }] : accion.movimientos;
        const advertencias: string[] = [];
        let hechos = 0;
        for (const m of movs) {
          try {
            const r = await sysadminService.moverAudioAsesoria(m.audio.audioId, m.destinoId);
            hechos++;
            advertencias.push(...r.advertencias.map((a) => `Audio #${m.audio.audioId}: ${a}`));
          } catch (err: any) {
            advertencias.push(`Audio #${m.audio.audioId}: no se movió (${err.message || 'error'})`);
          }
        }
        setResultado({
          ok: hechos === movs.length,
          texto: `${hechos} de ${movs.length} audio(s) movidos. Las horas se ajustaron solo por los audios movidos.`,
          advertencias,
        });
      }
      setAccion(null);
      await cargar();
    } catch (err: any) {
      setResultado({ ok: false, texto: err.message || 'No se pudo aplicar el cambio', advertencias: [] });
      setAccion(null);
    } finally {
      setAplicando(false);
    }
  };

  if (isLoading && !data) return <LoadingContainer text="Buscando asesorías con audios de otro asesor o sin audios..." />;

  if (error || !data) {
    return (
      <Card className="p-8 text-center text-red-600 bg-red-50 border-red-200">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="font-semibold text-sm mb-4">{error || 'No se pudieron cargar los casos'}</p>
        <Button onClick={cargar} variant="outline" size="sm">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reintentar
        </Button>
      </Card>
    );
  }

  const cuenta = (tipos: TipoCasoCorreccion[]) => data.casos.filter((c) => tipos.includes(c.tipo)).length;
  const audiosAjenos = data.casos.flatMap((c) => c.audios.filter((a) => !a.esDelDueno)).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <p className="text-sm text-gray-500 max-w-3xl">
            Audios subidos a la asesoría equivocada o por otro asesor, y asesorías que quedaron sin audios. Mueve cada audio a la asesoría
            correcta de quien lo subió o cambia el dueño cuando fue un cambio de asesor. Los cambios se aplican en producción al confirmar.
          </p>
        </div>
        <Button onClick={cargar} variant="outline" size="sm" disabled={isLoading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ['Asesorías con audios ajenos', cuenta(['AUDIOS_AJENOS']), 'text-red-700 bg-red-50/60 border-red-200'],
          ['Audios por revisar', audiosAjenos, 'text-amber-700 bg-amber-50/60 border-amber-200'],
          ['Finalizadas sin audios', cuenta(['FINALIZADA_SIN_AUDIOS']), 'text-amber-700 bg-amber-50/60 border-amber-200'],
          [
            'En revisión / en curso sin audios',
            cuenta(['REVISION_SIN_AUDIOS', 'EN_CURSO_ANTIGUA']),
            'text-blue-700 bg-blue-50/60 border-blue-200',
          ],
        ].map(([label, valor, cls]) => (
          <Card key={label as string} className={`p-4 ${cls}`}>
            <span className="text-[11px] uppercase tracking-wider font-bold">{label}</span>
            <p className="text-2xl font-black text-gray-900 mt-1 tabular-nums">{valor}</p>
          </Card>
        ))}
      </div>

      {resultado && (
        <div
          className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
            resultado.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {resultado.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="flex-1">
            <p className="font-medium">{resultado.texto}</p>
            {resultado.advertencias.length > 0 && (
              <ul className="mt-1 text-xs space-y-0.5">
                {resultado.advertencias.map((a) => (
                  <li key={a}>• {a}</li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" className="text-xs underline" onClick={() => setResultado(null)}>
            Cerrar
          </button>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex border-b border-gray-200 gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`pb-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label} <span className="font-mono text-xs text-gray-400">{cuenta(t.tipos)}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar empresa, asesor o #ID..."
            className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-64 focus:outline-2 focus:outline-gray-900"
          />
        </div>
        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(abrirId);
            if (Number.isInteger(n) && n > 0) setGestionId(n);
          }}
        >
          <input
            type="number"
            min={1}
            value={abrirId}
            onChange={(e) => setAbrirId(e.target.value)}
            placeholder="Abrir asesoría #ID"
            aria-label="Abrir una asesoría por su ID"
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs w-44 focus:outline-2 focus:outline-gray-900"
          />
          <Button type="submit" size="sm" variant="outline" disabled={!abrirId}>
            Gestionar
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!abrirId}
            title="Pasar los audios de esta asesoría a otra de la misma empresa"
            onClick={() => {
              const n = Number(abrirId);
              if (Number.isInteger(n) && n > 0) setAbsorberId(n);
            }}
          >
            Absorber…
          </Button>
        </form>
      </div>

      <div className="space-y-4">
        {casosTab.length === 0 ? (
          <Card className="p-8 text-center text-gray-500 text-sm">
            {q ? 'No hay casos con esa búsqueda.' : 'No hay casos de este tipo. Todo en orden.'}
          </Card>
        ) : (
          casosTab.map((c) =>
            c.tipo === 'AUDIOS_AJENOS' ? (
              <CasoAjenos
                key={c.asesoriaId}
                caso={c}
                nombre={nombre}
                asesores={data.asesores}
                onAccion={setAccion}
                onGestionar={setGestionId}
              />
            ) : (
              <CasoSinAudios
                key={c.asesoriaId}
                caso={c}
                nombre={nombre}
                asesores={data.asesores}
                posibles={posibles.get(c.asesoriaId) ?? []}
                onAccion={setAccion}
                onGestionar={setGestionId}
                onIr={(id) => {
                  setTab('ajenos');
                  setQ(String(id));
                }}
              />
            )
          )
        )}
      </div>

      {absorberId != null && (
        <GestionAsesoriaModal
          key={`absorber-${absorberId}`}
          modo="absorber"
          asesoriaId={absorberId}
          nombres={nombres}
          asesores={data.asesores}
          onClose={() => setAbsorberId(null)}
          onCambio={cargar}
        />
      )}

      {gestionId != null && (
        <GestionAsesoriaModal
          key={gestionId}
          asesoriaId={gestionId}
          nombres={nombres}
          asesores={data.asesores}
          onClose={() => setGestionId(null)}
          onCambio={cargar}
        />
      )}

      <Modal
        isOpen={!!accion}
        onClose={() => !aplicando && setAccion(null)}
        title={accion?.tipo === 'dueno' ? 'Cambiar dueño de la asesoría' : 'Mover audio(s) de asesoría'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAccion(null)} disabled={aplicando}>
              Cancelar
            </Button>
            <Button onClick={aplicar} isLoading={aplicando}>
              Aplicar en producción
            </Button>
          </div>
        }
      >
        {accion && <ResumenAccion accion={accion} nombre={nombre} />}
      </Modal>
    </div>
  );
}

function EncabezadoCaso({
  caso,
  nombre,
  extra,
  onGestionar,
}: {
  caso: CasoCorreccionDTO;
  nombre: (id: number | null) => string;
  extra?: React.ReactNode;
  onGestionar?: (asesoriaId: number) => void;
}) {
  const [estadoTxt, estadoVar] = ESTADO[caso.estado] ?? [caso.estado, 'gray'];
  return (
    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex flex-col md:flex-row md:items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-xs text-gray-400">#{caso.asesoriaId}</span>
          <span className="font-bold text-sm text-gray-900">{caso.empresa}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-1 tabular-nums">
          <span>{ordenTxt(caso.orden)}</span>
          <span>
            Inicio <strong className="text-gray-700">{caso.fechaInicio}</strong>
          </span>
          <span>
            Dueño <strong className="text-gray-700">{nombre(caso.asesorId)}</strong>
          </span>
          {caso.titularEmpresaId && caso.titularEmpresaId !== caso.asesorId && (
            <span>
              Titular de la empresa <strong className="text-gray-700">{nombre(caso.titularEmpresaId)}</strong>
            </span>
          )}
          <span>
            Horas <strong className="text-gray-700">{horasTxt(caso.horas)}</strong>
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 md:justify-end">
        <Badge variant={estadoVar} className="text-[11px]">
          {estadoTxt}
        </Badge>
        {extra}
        {onGestionar && (
          <Button size="sm" variant="outline" onClick={() => onGestionar(caso.asesoriaId)}>
            Gestionar
          </Button>
        )}
      </div>
    </div>
  );
}

function SelectorDueno({
  caso,
  asesores,
  sugeridos,
  nombre,
  onAccion,
}: {
  caso: CasoCorreccionDTO;
  asesores: CasosCorreccionResponse['asesores'];
  sugeridos: number[];
  nombre: (id: number | null) => string;
  onAccion: (a: Accion) => void;
}) {
  const [asesorId, setAsesorId] = useState('');
  const resto = asesores.filter((a) => a.asesorId !== caso.asesorId && !sugeridos.includes(a.asesorId));
  return (
    <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2 text-xs">
      <UserCog className="w-4 h-4 text-gray-400" />
      <label htmlFor={`dueno-${caso.asesoriaId}`} className="font-semibold text-gray-700">
        Dueño
      </label>
      <select
        id={`dueno-${caso.asesoriaId}`}
        value={asesorId}
        onChange={(e) => setAsesorId(e.target.value)}
        className="border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 max-w-xs"
      >
        <option value="">Mantener a {nombre(caso.asesorId)}</option>
        {sugeridos.length > 0 && (
          <optgroup label="Subieron audios a esta asesoría">
            {sugeridos.map((id) => (
              <option key={id} value={id}>
                {nombre(id)}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label="Todos los asesores">
          {resto.map((a) => (
            <option key={a.asesorId} value={a.asesorId}>
              {a.nombre}
              {a.activo ? '' : ' (inactivo)'}
            </option>
          ))}
        </optgroup>
      </select>
      <Button
        size="sm"
        variant="outline"
        disabled={!asesorId}
        onClick={() => onAccion({ tipo: 'dueno', caso, asesorId: Number(asesorId) })}
      >
        Cambiar dueño
      </Button>
      <span className="text-gray-400">Para cambios de asesor que no se corrigieron.</span>
    </div>
  );
}

function CasoAjenos({
  caso,
  nombre,
  asesores,
  onAccion,
  onGestionar,
}: {
  caso: CasoCorreccionDTO;
  nombre: (id: number | null) => string;
  asesores: CasosCorreccionResponse['asesores'];
  onAccion: (a: Accion) => void;
  onGestionar: (asesoriaId: number) => void;
}) {
  const [destinos, setDestinos] = useState<Record<number, string>>({});
  const ajenos = caso.audios.filter((a) => !a.esDelDueno);
  const ningunoDelDueno = ajenos.length === caso.audios.length;
  const subieron = [...new Set(ajenos.map((a) => a.subioId))];
  const mismoDia = ajenos
    .map((audio) => ({ audio, unicos: audio.candidatos.filter((c) => c.dias <= 1) }))
    .filter((x) => x.unicos.length === 1)
    .map((x) => ({ audio: x.audio, destinoId: x.unicos[0]!.asesoriaId }));

  return (
    <Card className="overflow-hidden border-gray-200">
      <EncabezadoCaso
        caso={caso}
        nombre={nombre}
        onGestionar={onGestionar}
        extra={
          <Badge variant={ningunoDelDueno ? 'red' : 'warning'} className="text-[11px]">
            {ningunoDelDueno ? 'Ningún audio es del dueño' : 'Audios del dueño y de otros'}
          </Badge>
        }
      />
      <SelectorDueno caso={caso} asesores={asesores} sugeridos={subieron} nombre={nombre} onAccion={onAccion} />
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left">
          <thead className="text-gray-500 font-semibold border-b border-gray-100">
            <tr>
              <th className="px-4 py-2">Audio</th>
              <th className="px-3 py-2">Grabación</th>
              <th className="px-3 py-2">Duración</th>
              <th className="px-3 py-2 min-w-[180px]">Subido por</th>
              <th className="px-3 py-2 min-w-[320px]">Mover a</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {caso.audios.map((au) => {
              const valor = destinos[au.audioId] ?? '';
              const esOtro = valor.startsWith('id:');
              const destinoId = esOtro ? Number(valor.slice(3)) : Number(valor);
              return (
                <tr key={au.audioId} className={au.esDelDueno ? '' : 'bg-amber-50/20'}>
                  <td className="px-4 py-2.5">
                    <span className="font-bold text-gray-900">#{au.audioId}</span>
                    {au.url && (
                      <a
                        href={au.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        Escuchar <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">{au.fechaGrabacion}</td>
                  <td className="px-3 py-2.5 font-mono whitespace-nowrap">{dur(au.duracionSeg)}</td>
                  <td className="px-3 py-2.5">
                    {nombre(au.subioId)}
                    {au.esDelDueno && (
                      <Badge variant="green" className="ml-1 text-[10px]">
                        dueño
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {au.esDelDueno ? (
                      <span className="text-gray-400">Es del dueño, no requiere cambio</span>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex gap-2">
                          <select
                            aria-label={`Destino del audio ${au.audioId}`}
                            value={esOtro ? 'otro' : valor}
                            onChange={(e) =>
                              setDestinos((d) => ({ ...d, [au.audioId]: e.target.value === 'otro' ? 'id:' : e.target.value }))
                            }
                            className="flex-1 min-w-0 w-0 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700"
                          >
                            <option value="">Elegir asesoría…</option>
                            {au.candidatos.length > 0 && (
                              <optgroup label={`Asesorías de ${nombre(au.subioId)} (★ = misma fecha)`}>
                                {au.candidatos.map((c) => (
                                  <option key={c.asesoriaId} value={c.asesoriaId}>
                                    {c.dias <= 1 ? '★ ' : ''}#{c.asesoriaId} {c.empresa} · {ordenTxt(c.orden)} · inicia {c.fechaInicio} (
                                    {diasTxt(c.dias)}) · {c.audiosVigentes} audio(s)
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            <option value="otro">Otra asesoría por ID…</option>
                          </select>
                          <Button size="sm" disabled={!destinoId} onClick={() => onAccion({ tipo: 'mover', audio: au, caso, destinoId })}>
                            Mover
                          </Button>
                        </div>
                        {esOtro && (
                          <input
                            type="number"
                            min={1}
                            aria-label="ID de la asesoría destino"
                            placeholder="ID de la asesoría"
                            value={valor.slice(3)}
                            onChange={(e) => setDestinos((d) => ({ ...d, [au.audioId]: `id:${e.target.value}` }))}
                            className="w-40 border border-gray-200 rounded-lg px-2.5 py-1.5 font-mono"
                          />
                        )}
                        {au.candidatos.length === 0 && (
                          <span className="text-[11px] text-gray-500">Sin asesorías propias: posible cambio de asesor (ver "Dueño").</span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mismoDia.length > 0 && (
        <div className="px-4 py-2.5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
          <span>{mismoDia.length} audio(s) tienen una única asesoría de quien lo subió que empieza el mismo día o el anterior.</span>
          <Button size="sm" onClick={() => onAccion({ tipo: 'moverVarios', caso, movimientos: mismoDia })}>
            Mover según la fecha ({mismoDia.length})
          </Button>
        </div>
      )}
    </Card>
  );
}

function CasoSinAudios({
  caso,
  nombre,
  asesores,
  posibles,
  onAccion,
  onGestionar,
  onIr,
}: {
  caso: CasoCorreccionDTO;
  nombre: (id: number | null) => string;
  asesores: CasosCorreccionResponse['asesores'];
  posibles: { audioId: number; desde: number; fecha: string; dias: number }[];
  onAccion: (a: Accion) => void;
  onGestionar: (asesoriaId: number) => void;
  onIr: (asesoriaId: number) => void;
}) {
  const cercanos = [...posibles].sort((a, b) => a.dias - b.dias).slice(0, 10);
  return (
    <Card className="overflow-hidden border-gray-200">
      <EncabezadoCaso
        caso={caso}
        nombre={nombre}
        onGestionar={onGestionar}
        extra={
          <>
            <Badge variant="warning" className="text-[11px]">
              {TIPO_LABEL[caso.tipo]}
            </Badge>
            {caso.audiosNoVigentes > 0 && (
              <Badge variant="red" className="text-[11px]">
                {caso.audiosNoVigentes} audio(s) rechazados o eliminados
              </Badge>
            )}
            {caso.horas >= 0.01 && (
              <Badge variant="red" className="text-[11px]">
                {horasTxt(caso.horas)} sin audios válidos
              </Badge>
            )}
          </>
        }
      />
      <SelectorDueno caso={caso} asesores={asesores} sugeridos={[]} nombre={nombre} onAccion={onAccion} />
      <div className="px-4 py-3 text-xs">
        <p className="font-semibold text-gray-700 mb-1.5">Audios mal ubicados que podrían ser de esta asesoría</p>
        {cercanos.length === 0 ? (
          <p className="text-gray-400">Ningún audio mal ubicado apunta a esta asesoría por fecha.</p>
        ) : (
          <ul className="space-y-1">
            {cercanos.map((p) => (
              <li key={`${p.audioId}-${p.desde}`} className="flex flex-wrap items-center gap-2 tabular-nums">
                <span className="font-mono font-bold">#{p.audioId}</span>
                <span>{p.fecha}</span>
                <span className="text-gray-400">({diasTxt(p.dias)})</span>
                <button
                  type="button"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  onClick={() => onIr(p.desde)}
                >
                  está en la asesoría #{p.desde} <ArrowRight className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function ResumenAccion({ accion, nombre }: { accion: Accion; nombre: (id: number | null) => string }) {
  if (accion.tipo === 'dueno') {
    return (
      <div className="space-y-2 text-sm text-gray-700">
        <p>
          La asesoría <strong>#{accion.caso.asesoriaId}</strong> ({accion.caso.empresa}) pasa de{' '}
          <strong>{nombre(accion.caso.asesorId)}</strong> a <strong>{nombre(accion.asesorId)}</strong>.
        </p>
        <p className="text-xs text-gray-500">No cambia los audios, las horas ni el asesor titular de la empresa.</p>
      </div>
    );
  }
  const movs = accion.tipo === 'mover' ? [{ audio: accion.audio, destinoId: accion.destinoId }] : accion.movimientos;
  const candidatoDe = (audio: AudioCasoDTO, id: number) => audio.candidatos.find((c) => c.asesoriaId === id);
  return (
    <div className="space-y-3 text-sm text-gray-700">
      <ul className="space-y-1.5">
        {movs.map(({ audio, destinoId }) => {
          const c = candidatoDe(audio, destinoId);
          return (
            <li key={audio.audioId} className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-mono font-bold">Audio #{audio.audioId}</span>
              <span className="text-gray-400">
                ({audio.fechaGrabacion}, {dur(audio.duracionSeg)})
              </span>
              <span>#{accion.caso.asesoriaId}</span>
              <ArrowRight className="w-3 h-3" />
              <strong>
                #{destinoId}
                {c ? ` ${c.empresa}` : ''}
              </strong>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-gray-500">
        Se cambia la asesoría y la empresa del audio. Si alguna de las dos asesorías está finalizada, sus horas se ajustan solo por la
        duración del audio, y el total de horas de cada empresa cambia solo en lo que aporta este audio. Si el destino es otra empresa, se
        intenta mover el archivo a su carpeta "Audios" en Drive. No se puede mover hacia ni desde una asesoría en revisión del admin.
      </p>
    </div>
  );
}
